import { useCallback, useEffect, useRef, useState } from "react";
import { importScript } from "assets-dynamic-import";

import {
	DEFAULT_GOOGLE_RECAPTCHA_GLOBAL_NAME,
	CASTLE_URL,
	DEFAULT_CASTLE_GLOBAL_NAME,
	getGoogleRecaptchaUrl,
} from "./settings";
import { prepareGoogleRecaptcha, addStyle } from "./utils";

export default function useAnyCaptcha(cb, { googleRecaptchaSiteKey, castlePublicKey, action }) {
	if (!googleRecaptchaSiteKey || !castlePublicKey) {
		return { action: cb, type: null };
	}

	const [type, setType] = useState("");
	const typeRef = useRef(null);
	const googleRecaptchaRef = useRef(null);
	const castleRef = useRef(null);

	useEffect(() => {
		importScript(
			getGoogleRecaptchaUrl(googleRecaptchaSiteKey),
			() => global[DEFAULT_GOOGLE_RECAPTCHA_GLOBAL_NAME],
		)
			.then(prepareGoogleRecaptcha)
			.then(googleRecaptcha => {
				setType("google-recaptcha");
				typeRef.current = "google-recaptcha";

				googleRecaptchaRef.current = googleRecaptcha;
				addStyle(".grecaptcha-badge { visibility: hidden; }");
			})
			.catch(() => {
				importScript(CASTLE_URL, () => global[DEFAULT_CASTLE_GLOBAL_NAME]).then(castle => {
					setType("castle");
					typeRef.current = "castle";

					castleRef.current = castle;
					castle.configure({ pk: castlePublicKey });
				});
			});
	}, [googleRecaptchaSiteKey, castlePublicKey]);

	const wrappedCb = useCallback(
		async (...args) => {
			if (typeRef.current === "castle") {
				const castle = castleRef.current;

				if (castle == null) throw new TypeError("Castle is not initialized correctly");

				const token = await castle.createRequestToken();

				return cb(...args, token, "castle");
			}

			if (typeRef.current === "google-recaptcha") {
				const grecaptcha = googleRecaptchaRef.current;

				if (grecaptcha == null)
					throw new TypeError("Google Recaptcha is not initialized correctly");

				return grecaptcha
					.execute(googleRecaptchaSiteKey, { action })
					.then(token => cb(...args, token, "google-recaptcha"));
			}

			return cb(...args);
		},
		[googleRecaptchaSiteKey, castlePublicKey, cb, type],
	);

	return {
		type,
		action: wrappedCb,
	};
}
